import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Avatar,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  Add,
  BarChartOutlined,
  ContentCopyOutlined,
  DeleteOutline,
  EditOutlined,
  LocalOfferOutlined,
  MoreVert,
  PauseOutlined,
  PaymentsOutlined,
  PeopleAltOutlined,
  PlayArrowOutlined,
  ReceiptLongOutlined,
  Search,
  ShowChartOutlined,
} from "@mui/icons-material";
import errorHandler from "../../utils/errorHandler";
import moment from "moment";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import AppExportButton from "../../lib/components/AppExportButton";
import DataTable from "../../lib/components/DataTable";
import ConfirmationDialog from "../../lib/components/ConfirmationDialog";
import useDebounce from "../../hooks/useDebounce";
import pathnames from "../../constants/pathnames";
import promoCodeService from "../../service/promoCodeService";
import customPlanService from "../../service/customPlanService";
import { moneyFormat } from "../../utils/moneyFormat";
import DateRangePreset, {
  DATE_PRESETS,
  DATE_PRESET_OPTIONS,
  normalizeDatePreset,
} from "../transmissions/components/DateRangePreset";
import PromoStatusBadge from "./components/promo-status-badge";

const PROMO_DATE_PRESET_OPTIONS = DATE_PRESET_OPTIONS.filter(
  (opt) => opt.value !== DATE_PRESETS.TODAY
);

/**
 * Map DateRangePreset enum / custom dates to the backend's analytics endpoint query.
 * Per backend doc: range ∈ '7D' | '30D' | '90D' | '1Y' (default '30D').
 *
 * THIS_MONTH  → calendar month view: dateFrom = first of current month, dateTo = today.
 *               The backend must constrain its series to this window.
 * LAST_30_DAYS → rolling 30-day window (no date override needed).
 */
export function mapPresetToAnalyticsQuery(preset, customFrom, customTo) {
  const normalized = normalizeDatePreset(preset);
  let range = "30D";
  let dateFrom = null;
  let dateTo = null;

  switch (normalized) {
    case DATE_PRESETS.LAST_7_DAYS:
      range = "7D";
      break;


    case DATE_PRESETS.THIS_MONTH:
      // Calendar month: Sep 1 → today. X-axis should read 1, 2, 3 … <today's date>
      range = "30D";
      dateFrom = moment().startOf("month").format("YYYY-MM-DD");
      dateTo = moment().format("YYYY-MM-DD");
      break;

    case DATE_PRESETS.LAST_30_DAYS:
      // Rolling 30-day window — no date override; backend handles the window
      range = "30D";
      break;

    case DATE_PRESETS.LAST_90_DAYS:
      range = "90D";
      break;

    case DATE_PRESETS.THIS_YEAR:
      range = "1Y";
      break;

    case DATE_PRESETS.ALL_TIME:
      // Backend has its own ALL_TIME provision — just send datePreset, no range override.
      // The backend will return all data from the beginning of time.
      range = null;
      break;


    case DATE_PRESETS.CUSTOM: {
      if (customFrom && customTo) {
        dateFrom = moment(customFrom).format("YYYY-MM-DD");
        dateTo = moment(customTo).format("YYYY-MM-DD");
        const diffDays = Math.abs(moment(customTo).diff(moment(customFrom), "days"));
        if (diffDays <= 7) range = "7D";
        else if (diffDays <= 30) range = "30D";
        else if (diffDays <= 90) range = "90D";
        else range = "1Y";
      } else {
        range = "30D";
      }
      break;
    }

    default:
      range = "30D";
  }

  const query = {};
  if (range) query.range = range;           // ALL_TIME sends no range — backend decides
  if (dateFrom) query.dateFrom = dateFrom;
  if (dateTo) query.dateTo = dateTo;
  if (normalized) query.datePreset = normalized;

  return query;
}


const formatNairaNoDecimals = (val) => {
  return `₦${Number(val || 0).toLocaleString("en-NG")}`;
};

export const resolvePromoStatus = (item) => {
  if (!item) return "ACTIVE";

  // 1. Explicitly paused
  if (item.status === "PAUSED" || item.isActive === false) {
    return "PAUSED";
  }

  const now = new Date();

  // 2. Future start date => SCHEDULED
  const startAt = item.schedule?.startAt || item.startDate;
  if (startAt && new Date(startAt).getTime() > now.getTime()) {
    return "SCHEDULED";
  }

  // 3. Past expiry date => EXPIRED
  const neverExpires = item.schedule?.neverExpires ?? false;
  const expiryAt = item.schedule?.expiryAt || item.endDate;
  if (!neverExpires && expiryAt && new Date(expiryAt).getTime() < now.getTime()) {
    return "EXPIRED";
  }

  // 4. Exhausted usage limit => DEPLETED
  const totalLimit = item.usage?.totalLimit ?? item.limit;
  const usedCount = item.redemptions ?? item.usage?.redeemedCount ?? item.usage ?? 0;
  if (totalLimit && totalLimit > 0 && usedCount >= totalLimit) {
    return "DEPLETED";
  }

  return item.status || "ACTIVE";
};

export const resolveValidityText = (item) => {
  if (!item) return "Ongoing";
  if (item.schedule?.neverExpires) return "Ongoing";

  const formatPart = (d) => {
    if (!d) return "";
    const m = moment(d);
    return m.isValid() ? m.format("DD-MM") : d;
  };

  if (item.schedule?.startAt && item.schedule?.expiryAt) {
    return `${formatPart(item.schedule.startAt)} → ${formatPart(item.schedule.expiryAt)}`;
  }
  if (item.startDate && item.endDate) {
    return `${formatPart(item.startDate)} → ${formatPart(item.endDate)}`;
  }
  if (item.validity) {
    return item.validity;
  }
  return "Ongoing";
};
const getDiscountText = (item) => {
  if (item.discount) return item.discount;
  const type = (item.discountType || "").toUpperCase();
  if (type === "PERCENTAGE") return `${item.amount}% off`;
  if (type === "FIXED") return `₦${Number(item.amount || 0).toLocaleString()} off`;
  if (type === "FIRST_MONTH_FREE") return "1st Month Free";
  if (type === "FREE_SETUP") return "100% Free Setup";
  if (type === "FREE_TRIAL") return "Free Trial";
  if (type === "CUSTOM") {
    if (item.amount) {
      return item.amountType === "FIXED"
        ? `₦${Number(item.amount).toLocaleString()} off (Custom)`
        : `${item.amount}% off (Custom)`;
    }
    return "Custom Offer";
  }
  return item.amount ? `${item.amount}% off` : "Special Offer";
};

const KNOWN_PLAN_NAMES = {
  "starter": "Starter",
  "professional": "Professional",
  "business": "Business",
  "enterprise": "Enterprise",
};

const STATUS_FILTER_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Active", value: "ACTIVE" },
  { label: "Paused", value: "PAUSED" },
  { label: "Scheduled", value: "SCHEDULED" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Depleted", value: "DEPLETED" },
];

const PLAN_FILTER_OPTIONS = [
  { label: "All Plans", value: "ALL" },
  { label: "Simple Start", value: "Simple Start" },
  { label: "Standard", value: "Standard" },
  { label: "Premium", value: "Premium" },
  { label: "Enterprise", value: "Enterprise" },
];

const renderApplicablePlans = (item, planNameMap = {}) => {
  const planList =
    Array.isArray(item.applicablePlans) && item.applicablePlans.length > 0
      ? item.applicablePlans
      : Array.isArray(item.plans) && item.plans.length > 0
      ? item.plans
      : null;

  if (item.planSelect === "ALL" || !planList || planList.length === 0) {
    return <Chip size="small" variant="outlined" label="All Plans" />;
  }

  return (
    <div className="d-flex flex-wrap gap-1">
      {planList.map((plan, i) => {
        let name = "";
        if (typeof plan === "object" && plan !== null) {
          name = plan.name || plan.title || "";
        } else if (typeof plan === "string") {
          const trimmed = plan.trim();
          if (planNameMap[trimmed]) {
            name = planNameMap[trimmed];
          } else if (KNOWN_PLAN_NAMES[trimmed]) {
            name = KNOWN_PLAN_NAMES[trimmed];
          } else if (KNOWN_PLAN_NAMES[trimmed.toLowerCase()]) {
            name = KNOWN_PLAN_NAMES[trimmed.toLowerCase()];
          } else if (/^[0-9a-fA-F]{24}$/.test(trimmed)) {
            // Unrecognized 24-character Mongo ObjectID fallback
            name = "Custom Tier";
          } else {
            name = trimmed;
          }
        }
        const formatted = name ? name.charAt(0).toUpperCase() + name.slice(1) : "Plan";
        return <Chip key={i} size="small" variant="outlined" label={formatted} />;
      })}
    </div>
  );
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);

export default function PromoCodesOverview() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch available plan metadata to resolve plan IDs to human-readable names
  const { data: plansResponse } = useQuery({
    queryKey: ["promo-code-available-plans"],
    queryFn: () => customPlanService.getPlans(),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const planNameMap = React.useMemo(() => {
    const map = { ...KNOWN_PLAN_NAMES };
    const raw = plansResponse?.data?.data || [];
    if (Array.isArray(raw)) {
      raw.forEach((p) => {
        const id = p._id || p.id;
        if (id && p.name) {
          map[id] = p.name;
        }
      });
    }
    return map;
  }, [plansResponse]);

  // Filters and state
  const [chartDatePreset, setChartDatePreset] = useState(DATE_PRESETS.THIS_MONTH);
  const [chartDateFrom, setChartDateFrom] = useState(null);
  const [chartDateTo, setChartDateTo] = useState(null);
  const [metricMode, setMetricMode] = useState("revenue"); // "revenue" | "redemptions"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedPlan, setSelectedPlan] = useState("ALL");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, item: null });
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const debouncedSearch = useDebounce(searchTerm);

  const handlePresetChange = (newPreset) => {
    setChartDatePreset(newPreset);
    if (newPreset !== DATE_PRESETS.CUSTOM) {
      setChartDateFrom(null);
      setChartDateTo(null);
    }
  };

  const handleCustomRangeChange = ({ dateFrom: from, dateTo: to }) => {
    setChartDatePreset(DATE_PRESETS.CUSTOM);
    setChartDateFrom(from);
    setChartDateTo(to);
  };

  // Query: Stats
  const statsQuery = useQuery({
    queryKey: ["promo-codes-overview-stats"],
    queryFn: () => promoCodeService.getOverviewStats(),
  });

  // Query: Analytics
  const analyticsQueryArgs = React.useMemo(() => {
    return mapPresetToAnalyticsQuery(chartDatePreset, chartDateFrom, chartDateTo);
  }, [chartDatePreset, chartDateFrom, chartDateTo]);

  const { data: analyticsData, isPending: isLoadingAnalytics } = useQuery({
    queryKey: ["promo-codes-analytics", analyticsQueryArgs],
    queryFn: () => promoCodeService.getAnalytics(analyticsQueryArgs),
  });

  // Query: Promo Codes Table
  const { data: codesData, isPending: isLoadingCodes } = useQuery({
    queryKey: ["promo-codes-list", debouncedSearch, selectedStatus, selectedPlan, page, limit],
    queryFn: () =>
      promoCodeService.getPromoCodes({
        keyword: debouncedSearch,
        status: selectedStatus === "ALL" ? undefined : selectedStatus,
        plan: selectedPlan === "ALL" ? undefined : selectedPlan,
        page,
        limit,
      }),
  });

  const stats = statsQuery.data || {};

  const displayedRevenue = React.useMemo(() => {
    if (analyticsData?.attributedGrossRevenue !== undefined && analyticsData?.attributedGrossRevenue !== null) {
      return analyticsData.attributedGrossRevenue;
    }
    return 0;
  }, [analyticsData]);

  const displayedRedemptions = React.useMemo(() => {
    if (analyticsData?.totalRedemptions !== undefined && analyticsData?.totalRedemptions !== null) {
      return analyticsData.totalRedemptions;
    }
    return stats?.redemptions ?? 0;
  }, [analyticsData, stats?.redemptions]);

  const chartData = React.useMemo(() => {
    const normalized = normalizeDatePreset(chartDatePreset);

    if (analyticsData?.series && Array.isArray(analyticsData.series) && analyticsData.series.length > 0) {
      let series = analyticsData.series;

      // THIS_YEAR / ALL_TIME — backend sends daily data even for range=1Y.
      // Aggregate daily → monthly buckets so we get at most 12 clean bars.
      if (normalized === DATE_PRESETS.THIS_YEAR || normalized === DATE_PRESETS.ALL_TIME) {
        // Backend sends rolling 365-day window (e.g. Sep 2025 → Sep 2026).
        // For THIS_YEAR, trim to Jan 1 of the current year so we only show this calendar year.
        if (normalized === DATE_PRESETS.THIS_YEAR) {
          const yearStart = moment().startOf("year");
          series = series.filter((s) => s.date && moment(s.date).isSameOrAfter(yearStart, "day"));
        }

        const monthlyMap = {};
        series.forEach((s) => {
          if (!s.date) return;
          const monthKey = moment(s.date).format("YYYY-MM");        // sort key
          const monthLabel = moment(s.date).format("MMM");           // display label (Jan, Feb…)
          if (!monthlyMap[monthKey]) {
            monthlyMap[monthKey] = { day: monthLabel, value: 0, rawDate: s.date, _key: monthKey };
          }
          monthlyMap[monthKey].value +=
            metricMode === "revenue" ? (s.revenue || 0) : (s.redemptions || 0);
        });
        return Object.values(monthlyMap)
          .sort((a, b) => a._key.localeCompare(b._key))
          .map(({ _key, ...rest }) => rest); // strip internal sort key
      }


      // THIS_MONTH — backend returns rolling 30D (ignores dateFrom/dateTo).
      // Trim to current calendar month only (Sep 1 → today).
      if (normalized === DATE_PRESETS.THIS_MONTH) {
        const monthStart = moment().startOf("month");
        series = series.filter((s) => s.date && moment(s.date).isSameOrAfter(monthStart, "day"));
      }

      // CUSTOM — backend ignores dateFrom/dateTo and returns its rolling window.
      // Trim series to the exact user-selected range on the frontend.
      if (normalized === DATE_PRESETS.CUSTOM && chartDateFrom && chartDateTo) {
        const from = moment(chartDateFrom).startOf("day");
        const to = moment(chartDateTo).endOf("day");
        series = series.filter(
          (s) => s.date && moment(s.date).isSameOrAfter(from) && moment(s.date).isSameOrBefore(to)
        );
      }

      // LAST_90_DAYS → "MMM D" labels; CUSTOM spanning > 31 days → "MMM D"; others → plain day number
      const diffDays = (normalized === DATE_PRESETS.CUSTOM && chartDateFrom && chartDateTo)
        ? Math.abs(moment(chartDateTo).diff(moment(chartDateFrom), "days"))
        : 0;
      const labelFormat =
        normalized === DATE_PRESETS.LAST_90_DAYS || (normalized === DATE_PRESETS.CUSTOM && diffDays > 31)
          ? "MMM D"
          : "D";

      return series.map((s, idx) => ({
        day: s.date ? moment(s.date).format(labelFormat) : (s.day || String(idx + 1)),
        value: metricMode === "revenue" ? (s.revenue || 0) : (s.redemptions || 0),
        rawDate: s.date,
      }));
    }

    return [];
  }, [analyticsData, metricMode, chartDatePreset, chartDateFrom, chartDateTo]);



  const maxValue = React.useMemo(() => {
    return Math.max(0, ...chartData.map((d) => d.value || 0));
  }, [chartData]);

  const { domain, ticks } = React.useMemo(() => {
    if (maxValue <= 120) {
      return {
        domain: [0, 120],
        ticks: [0, 20, 40, 60, 80, 100, 120],
      };
    }
    return {
      domain: [0, "auto"],
      ticks: undefined,
    };
  }, [maxValue]);

  const formatYAxisTick = (val) => {
    if (metricMode === "redemptions") {
      return Number(val).toLocaleString();
    }
    if (!val || val === 0) return "0";
    if (val >= 1000000) {
      const m = val / 1000000;
      return `₦${m % 1 === 0 ? m : m.toFixed(1)}M`;
    }
    if (val >= 1000) {
      const k = val / 1000;
      return `₦${k % 1 === 0 ? k : k.toFixed(1)}k`;
    }
    return Number(val).toLocaleString();
  };

  const codes = codesData?.docs || [];

  // Copy code handler
  const handleCopyCode = (code, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(code);
    toast.success(`Promo code "${code}" copied to clipboard!`);
  };

  const closeRowMenu = () => setRowMenu({ anchorEl: null, item: null });

  // Pause / Reactivate handler
  const handleTogglePause = async (item) => {
    closeRowMenu();
    const willBeActive = item.status === "PAUSED" || item.isActive === false;
    try {
      await promoCodeService.updatePromoCodeStatus(item.id, willBeActive);
      toast.success(
        willBeActive
          ? `Promo code "${item.code}" reactivated!`
          : `Promo code "${item.code}" paused.`
      );
      queryClient.invalidateQueries(["promo-codes-list"]);
      queryClient.invalidateQueries(["promo-codes-overview-stats"]);
    } catch (e) {
      errorHandler(e?.response || e);
    }
  };

  // Delete code handler
  const handleDeleteCode = async () => {
    if (!deleteItem) return;
    const { id, code } = deleteItem;
    setIsDeleting(true);
    try {
      await promoCodeService.deletePromoCode(id);
      toast.success(`Promo code "${code}" deleted.`);
      queryClient.invalidateQueries(["promo-codes-list"]);
      queryClient.invalidateQueries(["promo-codes-overview-stats"]);
      setDeleteItem(null);
    } catch (e) {
      errorHandler(e?.response || e);
    } finally {
      setIsDeleting(false);
    }
  };

  // Export CSV handler
  const handleExportCSV = async () => {
    try {
      await promoCodeService.exportPromoCodes({
        keyword: debouncedSearch || undefined,
        status: selectedStatus !== "ALL" ? selectedStatus : undefined,
        plan: selectedPlan !== "ALL" ? selectedPlan : undefined,
      });
      toast.success("Promo codes exported successfully!");
    } catch (e) {
      errorHandler(e?.response || e);
    }
  };

  const kpiCards = [
    { label: "Codes Generated", value: (stats.codesGenerated ?? 0).toLocaleString(), Icon: LocalOfferOutlined },
    { label: "Active Codes", value: (stats.activeCodes ?? 0).toLocaleString(), Icon: ShowChartOutlined },
    {
      label: "Redemptions",
      value: (stats.redemptions ?? 0).toLocaleString(),
      meta: stats.redemptionsGrowth,
      Icon: PeopleAltOutlined,
    },
    { label: "Discount Given", value: formatNairaNoDecimals(stats.discountGiven ?? 0), Icon: ReceiptLongOutlined },
    {
      label: "Gross Revenue",
      value: formatNairaNoDecimals(stats.grossRevenue ?? (stats.revenueGenerated || 0) + (stats.discountGiven || 0)),
      Icon: PaymentsOutlined,
    },
  ];

  const columns = [
    {
      key: "code",
      header: "Promo Code",
      render: (value) => (
        <div className="d-flex align-items-center gap-1">
          <span className="fw-medium text-nowrap">{value}</span>
          <MuiTooltip title="Copy code" arrow>
            <IconButton size="small" onClick={(e) => handleCopyCode(value, e)}>
              <ContentCopyOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </MuiTooltip>
        </div>
      ),
    },
    { key: "discount", header: "Discount", render: (_, row) => <span className="text-nowrap">{getDiscountText(row)}</span> },
    { key: "applicablePlans", header: "Applicable Plans", render: (_, row) => renderApplicablePlans(row, planNameMap) },
    {
      key: "usage",
      header: "Usage",
      render: (_, row) => {
        const usedCount = row.redemptions ?? row.usage?.redeemedCount ?? row.usage ?? 0;
        const totalLimit = row.usage?.totalLimit ?? row.limit ?? 100;
        const usagePercent = Math.min(100, Math.round((usedCount / (totalLimit || 1)) * 100));
        const isDepleted = resolvePromoStatus(row) === "DEPLETED" || (totalLimit > 0 && usedCount >= totalLimit);

        return (
          <div style={{ minWidth: 100 }}>
            <div className="mb-1 text-nowrap">
              {usedCount} <span className="text-secondary">/ {totalLimit}</span>
            </div>
            <LinearProgress
              variant="determinate"
              value={usagePercent}
              color={isDepleted ? "error" : usagePercent >= 80 ? "warning" : "secondary"}
              sx={{ borderRadius: 10, height: 6, width: 100 }}
            />
          </div>
        );
      },
    },
    {
      key: "validity",
      header: "Validity",
      render: (_, row) => {
        const validityText = resolveValidityText(row);
        const isExpired = resolvePromoStatus(row) === "EXPIRED" || row.isExpired;
        if (!isExpired) return <span className="text-nowrap">{validityText}</span>;

        return (
          <span className="text-nowrap">
            {validityText.split("→")[0]} → <span className="text-danger">{validityText.split("→")[1] || "Expired"}</span>
          </span>
        );
      },
    },
    { key: "status", header: "Status", render: (_, row) => <PromoStatusBadge status={resolvePromoStatus(row)} /> },
    { key: "revenue", header: "Revenue", render: (value) => (value ? moneyFormat(value, "NGN") : "N/A") },
    {
      key: "createdBy",
      header: "Created By",
      render: (value) => (
        <div className="d-flex align-items-center gap-2 text-nowrap">
          <Avatar sx={{ width: 26, height: 26, fontSize: 11 }}>{value?.initials || "AD"}</Avatar>
          <span>{value?.name || "Admin"}</span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      width: 48,
      render: (_, row) => (
        <IconButton size="small" onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, item: row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const menuItem = rowMenu.item;
  const isMenuItemPaused = menuItem?.status === "PAUSED" || menuItem?.isActive === false;

  return (
    <AppLayout>
      <Header title="Promo Codes" description="Configure discounts, manage growth campaigns and track subscription revenue" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <div className="d-flex flex-wrap mb-4 justify-content-end align-items-center gap-3">
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => navigate(pathnames.PROMO_CODES_CREATE)}
          >
            Create Promo Code
          </Button>
        </div>

        <div className="row">
          {kpiCards.map(({ label, value, meta, Icon }) => (
            <div key={label} className="col-12 col-sm-6 col-xl mb-4">
              <Paper className="p-3 bg-white h-100">
                <div className="mb-3">{label}</div>
                <div className="d-flex justify-content-between align-items-end gap-2">
                  <div>
                    <div className="fs-4 fw-medium text-nowrap">
                      {statsQuery.isPending ? <Skeleton width={90} /> : value}
                    </div>
                    {meta && <div className="fs-sm text-success">{meta}</div>}
                  </div>
                  <Icon className="text-secondary" />
                </div>
              </Paper>
            </div>
          ))}
        </div>

        <Paper className="p-3 mb-4">
          <div className="d-flex flex-wrap gap-3 justify-content-between align-items-end mb-3">
            <div>
              <div className="fs-5 fw-medium">Performance Analytics</div>
              <div className="fs-sm">Revenue and redemptions attributed to promo codes</div>
            </div>
            <DateRangePreset
              datePreset={chartDatePreset}
              dateFrom={chartDateFrom}
              dateTo={chartDateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
              presetOptions={PROMO_DATE_PRESET_OPTIONS}
            />
          </div>

          <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={metricMode}
              onChange={(_, value) => value && setMetricMode(value)}
            >
              <ToggleButton value="revenue" sx={{ textTransform: "none" }}>
                <PaymentsOutlined fontSize="small" className="me-2" />
                Promo Revenue
              </ToggleButton>
              <ToggleButton value="redemptions" sx={{ textTransform: "none" }}>
                <BarChartOutlined fontSize="small" className="me-2" />
                Redemptions
              </ToggleButton>
            </ToggleButtonGroup>
            <div className="text-end">
              <div className="fs-sm text-secondary">
                {metricMode === "revenue" ? "Attributed Net Revenue" : "Total Redemptions"}
              </div>
              <div className="fs-4 fw-medium">
                {isLoadingAnalytics && !analyticsData ? (
                  <Skeleton width={140} />
                ) : metricMode === "revenue" ? (
                  formatNairaNoDecimals(displayedRevenue)
                ) : (
                  Number(displayedRedemptions).toLocaleString()
                )}
              </div>
            </div>
          </div>

          <div style={{ width: "100%", height: 300 }}>
            {isLoadingAnalytics && !analyticsData ? (
              <Skeleton variant="rounded" width="100%" height={300} />
            ) : chartData.length === 0 ? (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary bg-light rounded">
                No analytics data recorded for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  key={`${metricMode}-${chartDatePreset}`}
                  data={chartData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="day"
                    interval={
                      normalizeDatePreset(chartDatePreset) === DATE_PRESETS.THIS_YEAR ||
                      normalizeDatePreset(chartDatePreset) === DATE_PRESETS.ALL_TIME
                        ? 0
                        : "preserveStartEnd"
                    }
                  />
                  <YAxis
                    width={56}
                    allowDecimals={false}
                    domain={domain}
                    ticks={ticks}
                    tickFormatter={formatYAxisTick}
                  />
                  <Tooltip
                    formatter={(val) => [
                      metricMode === "revenue"
                        ? (val >= 1000 ? `₦${Number(val).toLocaleString()}` : `${val}`)
                        : `${val} uses`,
                      metricMode === "revenue" ? "Attributed Net Revenue" : "Redemptions",
                    ]}
                    labelFormatter={(label, items) => {
                      const raw = items?.[0]?.payload?.rawDate;
                      return raw ? moment(raw).format("MMMM D, YYYY") : `Day ${label}`;
                    }}
                  />
                  <Bar dataKey="value" fill="#6C63F0" maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Paper>

        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div className="d-flex flex-wrap gap-3 flex-grow-1">
            <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
              <AppTextField
                placeholder="Search code or description..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search className="text-secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div style={{ width: 180 }}>
              <AppSelectField
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setPage(1);
                }}
                items={STATUS_FILTER_OPTIONS}
              />
            </div>
            <div style={{ width: 180 }}>
              <AppSelectField
                value={selectedPlan}
                onChange={(e) => {
                  setSelectedPlan(e.target.value);
                  setPage(1);
                }}
                items={PLAN_FILTER_OPTIONS}
              />
            </div>
          </div>
          <AppExportButton onClick={handleExportCSV} />
        </div>

        <DataTable
          data={codes}
          columns={columns}
          isLoading={isLoadingCodes}
          selectable
          page={page}
          pageSize={limit}
          totalPages={codesData?.totalPages || 1}
          onPageChange={setPage}
          onPageSizeChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50]}
        />
      </div>

      <Menu anchorEl={rowMenu.anchorEl} open={Boolean(rowMenu.anchorEl)} onClose={closeRowMenu}>
        <MenuItem onClick={() => handleTogglePause(menuItem)}>
          <ListItemIcon>{isMenuItemPaused ? <PlayArrowOutlined fontSize="small" /> : <PauseOutlined fontSize="small" />}</ListItemIcon>
          {isMenuItemPaused ? "Reactivate Code" : "Pause Code"}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            handleCopyCode(menuItem?.code, e);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlined fontSize="small" />
          </ListItemIcon>
          Copy Code
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeRowMenu();
            navigate(`${pathnames.PROMO_CODES_CREATE}?id=${menuItem?.id}`);
          }}
        >
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          Edit Code
        </MenuItem>
        <MenuItem
          className="text-danger"
          onClick={() => {
            setDeleteItem(menuItem);
            closeRowMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" color="error" />
          </ListItemIcon>
          Delete Code
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        isOpen={Boolean(deleteItem)}
        type="danger"
        onClose={() => setDeleteItem(null)}
        title="Delete promo code"
        body={`Are you sure you want to delete promo code <strong>${escapeHtml(deleteItem?.code)}</strong>?`}
        actionTitle="Delete"
        isLoading={isDeleting}
        onSubmit={handleDeleteCode}
      />
    </AppLayout>
  );
}
